import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { assets } from "@core/assets";
import Input from "../Input";
import Image from "../Image";

const DZ_PREFIX = "+213";
const DZ_COUNTRY_DIGITS = "213";
const DZ_DIGITS = 9; // digits after +213
const LEADING_ZERO = "0"; // display-only

type AlgerianPhoneInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange" | "leftIcon" | "inputMode"
> & {
  // stored in the form: "+213" + 9 digits OR ""
  value?: string;
  onChange?: (value: string) => void;
};

function formatDzDisplay(digitsAfterPrefix: string) {
  // digitsAfterPrefix: 9 digits max
  // display should be: 0xxx xx xx xx (prefix "+213" shown separately in left element)
  const s = digitsAfterPrefix.slice(0, DZ_DIGITS);

  // show leading 0 only if user started typing
  const local = s ? `${LEADING_ZERO}${s}` : "";

  // group: 4-2-2-2 (from local, which is 10 digits total when full)
  // example: 0777 94 14 13
  const g1 = local.slice(0, 4);
  const g2 = local.slice(4, 6);
  const g3 = local.slice(6, 8);
  const g4 = local.slice(8, 10);

  const groups = [g1, g2, g3, g4].filter(Boolean);
  return groups.join(" ");
}

function parseStoredValueToDigitsAfterPrefix(fullValue: string) {
  // fullValue might include spaces, etc.
  const digitsOnly = fullValue.replace(/\D/g, "");

  // remove 213 if present at the beginning
  let rest = digitsOnly;
  if (digitsOnly.startsWith(DZ_COUNTRY_DIGITS)) {
    rest = digitsOnly.slice(DZ_COUNTRY_DIGITS.length);
  }

  return rest.slice(0, DZ_DIGITS); // 9 digits after prefix
}

function AlgerianPhoneInput({
  value,
  onChange,
  ...rest
}: AlgerianPhoneInputProps) {
  // internal = 9 digits after +213 (what we store)
  const [digits, setDigits] = useState<string>("");

  // sync from outside
  useEffect(() => {
    if (!value) {
      setDigits("");
      return;
    }
    setDigits(parseStoredValueToDigitsAfterPrefix(value));
  }, [value]);

  const displayValue = useMemo(() => formatDzDisplay(digits), [digits]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // user sees: "+213 0777 94 14 13"
    // we extract digits from whatever they typed/pasted
    const allDigits = e.target.value.replace(/\D/g, "");

    // strip prefix if they included it (+213)
    let rest = allDigits;
    if (rest.startsWith(DZ_COUNTRY_DIGITS)) {
      rest = rest.slice(DZ_COUNTRY_DIGITS.length);
    }

    // if user typed the leading 0 (display), drop it (stored value doesn't include it)
    if (rest.startsWith("0")) {
      rest = rest.slice(1);
    }

    const limited = rest.slice(0, DZ_DIGITS);

    setDigits(limited);
    onChange?.(limited ? `${DZ_PREFIX}${limited}` : "");
  };

  // left element: flag + country code + divider (matches Figma)
  const Left = (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Image
        src={assets["dz-flag"]}
        className="w-5 h-[14px] object-cover rounded-sm flex-shrink-0"
      />
      <span className="text-sm font-medium text-secondary-900 whitespace-nowrap">
        +213
      </span>
      <div className="w-px h-4 bg-neutral-200 flex-shrink-0" />
    </div>
  );

  return (
    <Input
      {...rest}
      placeholder={rest.placeholder ?? "xxxxxxxxxx"}
      value={displayValue}
      onChange={handleChange}
      inputMode="numeric"
      className="pl-[5.5rem]"
      leftIcon={Left as unknown as string}
    />
  );
}

export default AlgerianPhoneInput;
